import { Component, Input } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * One article card, as the news lists render it.
 *
 * Three copies today - media-center/news in both trees and the home page's
 * news section - at ~14 lines each. They measured 85% identical to each other
 * and 98% across languages, and the differences were all defects rather than
 * intent:
 *
 *  - the image link used `href`, so clicking the picture did a full page
 *    reload while the title and Read more beside it routed client-side;
 *  - every image carried `alt="Lates Post"` - the same text on every card, and
 *    misspelled;
 *  - each card held three links to the same article, two of which announced
 *    the title, so a screen-reader user heard the headline twice per card and
 *    had to step past a link that went nowhere new.
 *
 * All three are fixed here once. The image is decorative - it sits inside a
 * card whose heading already names the article - so it takes an empty alt and
 * its link is hidden from assistive tech rather than repeating the title.
 */
@Component({
  selector: 'raksme-news-card',
  templateUrl: './news-card.component.html'
})
export class NewsCardComponent {

  /** The article row from the API. */
  @Input() news: any = {};

  /**
   * Language suffix the home page appends to the route ('' on the English
   * media-center list). Kept as an input rather than derived, because the two
   * callers genuinely differ and guessing would break one of them.
   */
  @Input() langUrl: string = '';

  /** CMS label for the read-more control. */
  @Input() readMoreLabel: string = '';

  constructor(private sanitizer: DomSanitizer) { }

  /**
   * Title and excerpt keep their HTML, sanitised here rather than through the
   * `safe` pipe - PageSectionsComponent does the same, so the shared module needs
   * no pipe module and the bypass stays in audited component code.
   */
  private trust(raw: any): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(
      raw === null || raw === undefined ? '' : String(raw));
  }

  get titleHtml(): SafeHtml { return this.trust(this.news && this.news['title']); }

  get contentHtml(): SafeHtml { return this.trust(this.news && this.news['content']); }

  get link(): string {
    const slug = this.news && this.news['url_rewrite'] ? this.news['url_rewrite'] : '';
    return '/media-center/news-articles/' + slug + (this.langUrl || '');
  }

  /**
   * Never let the only name for the read-more link depend on a fetch. The CMS
   * label vanishing has produced anonymous controls on this site four times;
   * the fallback is what stops it happening a fifth.
   */
  get readMore(): string {
    const l = this.readMoreLabel;
    return l && String(l).trim() ? String(l).trim() : 'Read more';
  }
}
